import store from "@src/reducers/store";
import {
  updateActiveMachineStructureNode,
  updateDeleteIconActiveState,
  updateFindBlueprintModalContentShown,
  updateIsBlueprintApplied,
  updateNodeSelectionState,
  updateSelectedFindBlueprint,
  updateShowBulkEditDataAcquisitionTypeIcon,
  updateShowFindBlueprintModal,
  updateStructureWasSaved,
} from "@src/reducers/your-reducer";
import { getLogger } from "@src/cloud-connect/dependency-injection-container/get-logger";
import { getMachineStructureNodeset } from "@src/cloud-connect/dependency-injection-container/get-machine-structure-nodeset";
import { getInitialStructureNodeset } from "@src/cloud-connect/dependency-injection-container/get-initial-structure-nodeset";
import { OurNode } from "@src/cloud-connect/poc/our-node";
import { OurNodeset } from "@src/cloud-connect/poc/our-nodeset";
import { TransferredComparer } from "@src/cloud-connect/poc/transferred-comparer";
import {
  getAlarmProfiles,
  getAlarmProfilesReferenceCopy,
} from "../dependency-injection-container/get-alarm-profiles";
import { ApiAlarmProfile } from "../blueprint/use-cases/new-save-blueprint-use-case";
import { parseAlarmProfiles } from "../alarms/entities/alarm-profiles-parser";

const newApplyBlueprint = (proposedBlueprintStructure: any) => {
  const updateCurrentAlarmProfiles = (
    proposedBlueprintStructureAlarmProfiles: ApiAlarmProfile[],
  ) => {
    const sourceId = store.getState().yourSlice.connectionView.source.source_id;
    const clientId =
      store.getState().yourSlice.connectionView.source.connected_via_client;

    const parsedAlarmProfiles = parseAlarmProfiles({
      response: proposedBlueprintStructureAlarmProfiles,
      getNodeById: getMachineStructureNodeset().getNodeById.bind(
        getMachineStructureNodeset(),
      ),
      OurNodeClass: OurNode,
      source_id: sourceId,
      client_id: clientId,
    });

    getAlarmProfiles().setAlarmProfiles(parsedAlarmProfiles.getAlarmProfiles());
    getAlarmProfilesReferenceCopy().setAlarmProfiles(
      parsedAlarmProfiles.getAlarmProfiles(),
    );
  };

  const closeModal = () => {
    store.dispatch(updateShowFindBlueprintModal(false));
    store.dispatch(updateFindBlueprintModalContentShown("find"));
    store.dispatch(updateSelectedFindBlueprint(undefined));
  };

  const updateReduxStores = () => {
    store.dispatch(updateActiveMachineStructureNode(""));
    store.dispatch(updateNodeSelectionState("none"));
    store.dispatch(updateStructureWasSaved(false));
    store.dispatch(updateDeleteIconActiveState(false));
    store.dispatch(updateShowBulkEditDataAcquisitionTypeIcon(false));
    store.dispatch(updateIsBlueprintApplied(true));
  };

  const updateBlueprintRootNodeWithInitialStructureRootNodeData = (
    blueprintFlatMap: Map<string, OurNode>,
    initialStructureFlatMap: Map<string, OurNode>,
  ): void => {
    blueprintFlatMap.forEach((blueprintNode) => {
      const initialStructureNode = initialStructureFlatMap.get(
        blueprintNode.id,
      );

      if (
        initialStructureNode !== undefined &&
        initialStructureNode.node_type === "VARIABLE"
      ) {
        if (blueprintNode.attributes === undefined) {
          blueprintNode.attributes = {
            data_acquisition_type:
              initialStructureNode.attributes.data_acquisition_type,
            data_type: initialStructureNode.attributes.data_type,
            data_type_as_string:
              initialStructureNode.attributes.data_type_as_string,
            last_known_value: initialStructureNode.attributes.last_known_value,
            last_known_value_timestamp:
              initialStructureNode.attributes.last_known_value_timestamp,
            node_class: initialStructureNode.attributes.node_class,
            node_class_as_string:
              initialStructureNode.attributes.node_class_as_string,
            sample_interval_in_seconds:
              initialStructureNode.attributes.sample_interval_in_seconds,
            unit: initialStructureNode.attributes.unit,
          };
        } else {
          blueprintNode.attributes.last_known_value =
            initialStructureNode.attributes.last_known_value;
          blueprintNode.attributes.last_known_value_timestamp =
            initialStructureNode.attributes.last_known_value_timestamp;
        }
      }
    });
  };

  const logger = getLogger();

  logger.debug("newApplyBlueprint", {
    proposedBlueprintStructure: proposedBlueprintStructure,
  });

  closeModal();

  // load data
  const initialStructureNodeset: OurNodeset = getInitialStructureNodeset();
  const machineStructureNodeset: OurNodeset = getMachineStructureNodeset();

  const blueprintRootNode: OurNode = OurNode.makeObjectTree(
    proposedBlueprintStructure.root_node,
  );

  blueprintRootNode.source_type =
    machineStructureNodeset.getRootNode().source_type;

  const blueprintRootNodeFlatMap: Map<string, OurNode> =
    blueprintRootNode.createMapFromOurNodeTree();

  const blueprintNodeset: OurNodeset = new OurNodeset({
    flatMap: blueprintRootNodeFlatMap,
    rootNode: blueprintRootNode,
  });

  // check if initial structure is loaded (both flatMap and rootNode must exist)
  const initialStructureFlatMap = initialStructureNodeset.getFlatMap();
  const initialStructureRootNode = initialStructureNodeset.getRootNode();
  const isInitialStructureLoaded =
    initialStructureFlatMap != null && initialStructureRootNode != null;

  if (!isInitialStructureLoaded) {
    logger.debug("applyBlueprint: Initial structure not loaded yet", {
      message:
        "Applying blueprint without initial structure data. Will update when initial structure loads.",
    });

    // observer to update machine structure once initial structure loads
    const updateMachineStructureOnceInitialStructureLoads = () => {
      const loadedInitialStructureFlatMap =
        initialStructureNodeset.getFlatMap();
      const loadedInitialStructureRootNode =
        initialStructureNodeset.getRootNode();

      if (
        loadedInitialStructureFlatMap &&
        loadedInitialStructureFlatMap.size > 0 &&
        loadedInitialStructureRootNode
      ) {
        // IMPORTANT: Remove observer FIRST to prevent infinite loop when we call setFlatMap
        initialStructureNodeset.removeObserver(
          updateMachineStructureOnceInitialStructureLoads,
        );

        // TODO: this is 99% likely loaded from service query timescale at request time, and taking it
        // from the initial structure is no longer needed. Commenting out for now but likely can be removed soon
        // updateBlueprintRootNodeWithInitialStructureRootNodeData(
        //   machineStructureNodeset.getFlatMap(),
        //   loadedInitialStructureFlatMap,
        // );

        // update transferred attributes on initial structure
        // this marks which nodes from initial structure are now in the machine structure
        new TransferredComparer(
          loadedInitialStructureFlatMap,
          machineStructureNodeset.getFlatMap(),
        ).setFoundNodeToTransferred();

        // trigger re-render of structures
        machineStructureNodeset.setFlatMap(
          machineStructureNodeset.getRootNode().createMapFromOurNodeTree(),
        );
        initialStructureNodeset.setFlatMap(
          loadedInitialStructureRootNode.createMapFromOurNodeTree(),
        );

        logger.debug(
          "applyBlueprint: Machine structure updated with live values from initial structure",
        );
      }
    };

    console.log("Adding observer to initialStructureNodeset");
    // add observer to watch for initial structure loading
    initialStructureNodeset.addObserver(
      updateMachineStructureOnceInitialStructureLoads,
    );
  } else {
    // transform data - update with initial structure data immediately if it's already loaded
    updateBlueprintRootNodeWithInitialStructureRootNodeData(
      blueprintNodeset.getFlatMap(),
      initialStructureFlatMap,
    );
  }

  logger.debug("updated blueprint nodeset", {
    blueprintNodeset: blueprintNodeset,
  });

  logger.debug("machineStructureNodeset before", {
    machineStructureNodeset: getMachineStructureNodeset(),
  });

  // this is necessary otherwise the countSelectedVariables function present in
  // useControlBarBulkModifyDataAcquisitionTypeButtonHook still relies on a stale
  // selected nodes IDs list and breaks
  machineStructureNodeset.flushSelectedNodeIdsWhenOverwritingNodeset();

  // apply / save data
  machineStructureNodeset.setRootNode(blueprintNodeset.getRootNode());
  machineStructureNodeset.setFlatMap(blueprintNodeset.getFlatMap());

  // apply alarm profiles if the blueprint contains any
  // (the check is to allow retrofit, many blueprints do not have alarm profiles and the parser would crash)
  if (
    proposedBlueprintStructure &&
    proposedBlueprintStructure.alarm_profiles &&
    proposedBlueprintStructure.alarm_profiles.length > 0
  ) {
    updateCurrentAlarmProfiles(proposedBlueprintStructure.alarm_profiles);
  }

  logger.debug("machineStructureNodeset after", {
    machineStructureNodeset: getMachineStructureNodeset(),
  });

  // update initial structure with transferred attributes and trigger re-render
  // only do this if initial structure is loaded, otherwise it's done via an observer when it loads
  if (isInitialStructureLoaded) {
    // initialstructure needs to be updated for transferred attribute
    new TransferredComparer(
      initialStructureFlatMap,
      machineStructureNodeset.getFlatMap(),
    ).setFoundNodeToTransferred();

    // reset initial structure flat map to trigger rendering
    initialStructureNodeset.setFlatMap(
      initialStructureRootNode.createMapFromOurNodeTree(),
    );
  }

  updateReduxStores();

  // TODO: update condition monitoring alarm profiles
};

export const applyBlueprint = (proposedBlueprintStructure: any) => {
  newApplyBlueprint(proposedBlueprintStructure);
};
